import { useCallback, useState } from 'react';

import type { FxFilesClient, Segment } from '../lib/client.js';

export interface UseFileUploadOptions {
  readonly client: FxFilesClient;
  readonly segment: Segment;
  readonly onComplete?: () => void;
}

export interface UseFileUpload {
  readonly uploading: boolean;
  readonly progress: number;
  readonly error: string | null;
  uploadFiles(files: readonly File[]): Promise<void>;
}

export function useFileUpload(opts: UseFileUploadOptions): UseFileUpload {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const uploadFiles = useCallback(
    async (files: readonly File[]): Promise<void> => {
      if (files.length === 0) return;
      setUploading(true);
      setError(null);
      setProgress(0);
      try {
        for (let i = 0; i < files.length; i += 1) {
          await opts.client.upload(opts.segment, files[i]!);
          setProgress(Math.round(((i + 1) / files.length) * 100));
        }
        opts.onComplete?.();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setUploading(false);
      }
    },
    [opts.client, opts.segment, opts.onComplete],
  );

  return { uploading, progress, error, uploadFiles };
}
